import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import CustomNavbar from './components/CustomNavbar';
import LeagueTable from './components/LeagueTable';
import GoalscorersTable from './components/GoalscorersTable';
import ResultsView from './components/ResultsView';
import FixturesView from './components/FixturesView';
import CardsView from './components/CardsView';
import CupView from './components/CupView';
import MatchDetails from './components/MatchDetails';
import TeamDetails from './components/TeamDetails';
import Login from './components/Login';
import News from './components/News';
import ProtectedRoute from './components/ProtectedRoute';
import AdminLayout from './admin/AdminLayout';
import AdminDashboard from './admin/AdminDashboard';
import AdminTeams from './admin/AdminTeams';
import AdminSeasons from './admin/AdminSeasons';
import AdminNews from './admin/AdminNews';
import Seo from './components/Seo';
import client from './api/client';
import 'bootstrap/dist/css/bootstrap.min.css';

const FALLBACK_SEASONS = [{ value: '2025-2026', label: '2025-2026' }];

// Per-route SEO metadata (unique title + description per page).
const SEO_META = {
    '/': { title: 'Aktualności', description: 'Najnowsze aktualności i komunikaty rozgrywek Średzka LZS.' },
    '/aktualnosci': { title: 'Aktualności', description: 'Najnowsze aktualności i komunikaty rozgrywek Średzka LZS.' },
    '/tabela': { title: 'Tabela', description: 'Tabela ligowa Średzka LZS — punkty, bilans bramek i pozycje drużyn.' },
    '/puchar': { title: 'Puchar', description: 'Puchar Średzka LZS — fazy grupowe i drabinka pucharowa.' },
    '/terminarz': { title: 'Terminarz', description: 'Terminarz meczów Średzka LZS — daty i godziny spotkań.' },
    '/wyniki': { title: 'Wyniki', description: 'Wyniki meczów Średzka LZS — rezultaty kolejek ligowych i pucharowych.' },
    '/strzelcy': { title: 'Strzelcy', description: 'Klasyfikacja strzelców Średzka LZS.' },
    '/kartki': { title: 'Kartki', description: 'Klasyfikacja kartek żółtych i czerwonych Średzka LZS.' },
    '/login': { title: 'Logowanie', description: 'Logowanie do panelu Średzka LZS.' },
};

const AppShell = () => {
    const location = useLocation();
    const isAdmin = location.pathname.startsWith('/admin');
    const meta = isAdmin ? { title: 'Panel administracyjny' } : SEO_META[location.pathname] || {};

    const [seasons, setSeasons] = useState(FALLBACK_SEASONS);
    const [season, setSeason] = useState(FALLBACK_SEASONS[0].value);

    // Populate the season selector from the backend, falling back gracefully.
    const loadSeasons = useCallback(() => {
        client
            .get('/seasons')
            .then((res) => {
                if (!Array.isArray(res.data)) return;
                // The public selector must never offer hidden seasons — even to a
                // logged-in admin browsing the public site (whose cookie makes the
                // API also return hidden seasons). Filter to visible ones here.
                const opts = res.data
                    .filter((s) => s.visible !== false)
                    .map((s) => ({ value: s.years, label: s.years }));
                if (opts.length) {
                    setSeasons(opts);
                    setSeason((prev) =>
                        opts.some((o) => o.value === prev) ? prev : opts[0].value
                    );
                }
            })
            .catch(() => {
                /* keep fallback seasons */
            });
    }, []);

    // Refresh on load and whenever the user returns from the admin panel, so a
    // visibility change made in the panel is reflected without a full reload.
    useEffect(() => {
        if (!isAdmin) loadSeasons();
    }, [isAdmin, loadSeasons]);

    return (
        <>
            <Seo title={meta.title} description={meta.description} path={location.pathname} />
            {!isAdmin && (
                <h1 className="visually-hidden">
                    {meta.title ? `${meta.title} — Średzka LZS` : 'Średzka LZS'}
                </h1>
            )}
            {!isAdmin && (
                <CustomNavbar season={season} onSeasonChange={setSeason} seasons={seasons} />
            )}
            <div className={isAdmin ? '' : 'container mt-3'}>
                <Routes>
                    <Route path="/" element={<News />} />
                    <Route path="/aktualnosci" element={<News />} />
                    <Route path="/tabela" element={<LeagueTable season={season} />} />
                    <Route path="/puchar" element={<CupView season={season} />} />
                    <Route path="/terminarz" element={<FixturesView season={season} />} />
                    <Route path="/wyniki" element={<ResultsView season={season} />} />
                    <Route path="/strzelcy" element={<GoalscorersTable season={season} />} />
                    <Route path="/kartki" element={<CardsView season={season} />} />
                    <Route path="/mecz/:matchId" element={<MatchDetails />} />
                    <Route path="/zespol/:teamId" element={<TeamDetails />} />
                    <Route path="/login" element={<Login />} />

                    <Route
                        path="/admin"
                        element={
                            <ProtectedRoute>
                                <AdminLayout />
                            </ProtectedRoute>
                        }
                    >
                        <Route index element={<AdminDashboard />} />
                        <Route path="teams" element={<AdminTeams />} />
                        <Route path="seasons" element={<AdminSeasons />} />
                        <Route path="news" element={<AdminNews />} />
                    </Route>
                </Routes>
            </div>
        </>
    );
};

const App = () => (
    <Router>
        <AppShell />
    </Router>
);

export default App;
